import type { FormField } from '../types';

export interface FormTemplate {
  id: string;
  name: string;
  description: string;
  category: 'registration' | 'evaluation' | 'feedback' | 'custom';
  fields: FormField[];
  createdAt: string;
  isSystem: boolean; // System templates cannot be deleted
}

// Reviewee Registration Template
export const revieweeRegistrationTemplate: FormTemplate = {
  id: 'reviewee-registration',
  name: 'Reviewee Registration Form',
  description: 'Complete registration form for portfolio review participants including personal info, academic background, and review goals',
  category: 'registration',
  isSystem: true,
  createdAt: new Date().toISOString(),
  fields: [
    // Personal Information Section
    {
      id: 'firstName',
      type: 'text',
      label: 'First Name',
      placeholder: 'Enter your first name',
      required: true
    },
    {
      id: 'lastName',
      type: 'text',
      label: 'Last Name',
      placeholder: 'Enter your last name',
      required: true
    },
    {
      id: 'email',
      type: 'email',
      label: 'Email Address',
      placeholder: 'your.email@example.com',
      required: true
    },
    {
      id: 'phone',
      type: 'text',
      label: 'Phone Number',
      placeholder: '(555) 123-4567',
      required: false
    },
    
    // Academic/Professional Background
    {
      id: 'currentStatus',
      type: 'select',
      label: 'Current Status',
      required: true,
      options: [
        'Undergraduate Student',
        'Graduate Student',
        'Recent Graduate',
        'Working Professional',
        'Career Changer',
        'Freelancer',
        'Other'
      ]
    },
    {
      id: 'institution',
      type: 'text',
      label: 'Institution/Company',
      placeholder: 'University name or company',
      required: false
    },
    {
      id: 'major',
      type: 'text',
      label: 'Major/Field of Study',
      placeholder: 'e.g., Graphic Design, Computer Science',
      required: false
    },
    {
      id: 'graduationDate',
      type: 'text',
      label: 'Expected Graduation Date',
      placeholder: 'MM/YYYY',
      required: false
    },
    {
      id: 'gpa',
      type: 'text',
      label: 'GPA (Optional)',
      placeholder: '3.5/4.0',
      required: false
    },
    
    // Experience & Skills
    {
      id: 'experienceLevel',
      type: 'select',
      label: 'Experience Level',
      required: true,
      options: [
        'Beginner (0-1 years)',
        'Intermediate (1-3 years)',
        'Advanced (3+ years)'
      ]
    },
    {
      id: 'focusAreas',
      type: 'checkbox',
      label: 'Focus Areas (Select all that apply)',
      required: true,
      options: [
        'UI/UX Design',
        'Graphic Design',
        'Web Development',
        'Mobile Development',
        'Data Science',
        'Product Management',
        'Marketing',
        'Content Strategy',
        'Branding',
        'Illustration',
        'Photography',
        'Video Production',
        'Other'
      ]
    },
    {
      id: 'portfolioUrl',
      type: 'text',
      label: 'Portfolio URL',
      placeholder: 'https://your-portfolio.com',
      required: false
    },
    {
      id: 'linkedinUrl',
      type: 'text',
      label: 'LinkedIn Profile',
      placeholder: 'https://linkedin.com/in/yourprofile',
      required: false
    },
    {
      id: 'githubUrl',
      type: 'text',
      label: 'GitHub Profile',
      placeholder: 'https://github.com/yourusername',
      required: false
    },
    {
      id: 'websiteUrl',
      type: 'text',
      label: 'Personal Website',
      placeholder: 'https://yourwebsite.com',
      required: false
    },
    
    // File Uploads
    {
      id: 'portfolio',
      type: 'file',
      label: 'Portfolio (PDF)',
      required: true
    },
    {
      id: 'resume',
      type: 'file',
      label: 'Resume (PDF)',
      required: true
    },
    
    // Review Goals
    {
      id: 'reviewType',
      type: 'select',
      label: 'What type of review are you seeking?',
      required: true,
      options: [
        'Portfolio Review Only',
        'Resume Review Only',
        'Both Portfolio & Resume',
        'Career Guidance & Mentorship'
      ]
    },
    {
      id: 'specificGoals',
      type: 'textarea',
      label: 'Specific Goals for this Review',
      placeholder: 'What do you hope to achieve from this review? What specific feedback are you looking for?',
      required: true
    },
    {
      id: 'challengeAreas',
      type: 'textarea',
      label: 'Areas you\'d like to improve',
      placeholder: 'What aspects of your work do you feel need the most improvement?',
      required: false
    },
    {
      id: 'careerGoals',
      type: 'textarea',
      label: 'Career Goals',
      placeholder: 'What are your short-term and long-term career goals?',
      required: false
    },
    
    // Agreements
    {
      id: 'agreeToTerms',
      type: 'checkbox',
      label: 'Terms & Agreements',
      required: true,
      options: [
        'I agree to the terms and conditions of this review program'
      ]
    },
    {
      id: 'agreeToRecording',
      type: 'checkbox',
      label: 'Recording Consent',
      required: false,
      options: [
        'I consent to the review session being recorded for quality assurance'
      ]
    },
    {
      id: 'agreeToFollow',
      type: 'checkbox',
      label: 'Attendance Agreement',
      required: true,
      options: [
        'I agree to attend my scheduled review session and provide 24-hour notice if I need to cancel'
      ]
    }
  ]
};

// Reviewer Registration Template
export const reviewerRegistrationTemplate: FormTemplate = {
  id: 'reviewer-registration',
  name: 'Reviewer Registration Form',
  description: 'Application form for industry professionals volunteering as portfolio reviewers',
  category: 'registration',
  isSystem: true,
  createdAt: new Date().toISOString(),
  fields: [
    // Personal Information
    {
      id: 'firstName',
      type: 'text',
      label: 'First Name',
      placeholder: 'Enter your first name',
      required: true
    },
    {
      id: 'lastName',
      type: 'text',
      label: 'Last Name',
      placeholder: 'Enter your last name',
      required: true
    },
    {
      id: 'email',
      type: 'email',
      label: 'Email Address',
      placeholder: 'your.email@company.com',
      required: true
    },
    {
      id: 'phone',
      type: 'text',
      label: 'Phone Number',
      placeholder: '(555) 123-4567',
      required: false
    },
    {
      id: 'linkedinUrl',
      type: 'text',
      label: 'LinkedIn Profile',
      placeholder: 'https://linkedin.com/in/yourprofile',
      required: false
    },
    {
      id: 'portfolioUrl',
      type: 'text',
      label: 'Portfolio/Company Website',
      placeholder: 'https://your-portfolio.com',
      required: false
    },
    
    // Professional Background
    {
      id: 'company',
      type: 'text',
      label: 'Current Company',
      placeholder: 'e.g., Google, Apple, Freelance',
      required: true
    },
    {
      id: 'position',
      type: 'text',
      label: 'Current Position',
      placeholder: 'e.g., Senior UX Designer, Product Manager',
      required: true
    },
    {
      id: 'yearsOfExperience',
      type: 'select',
      label: 'Years of Professional Experience',
      required: true,
      options: [
        '1-2 years',
        '3-5 years',
        '5-10 years',
        '10+ years'
      ]
    },
    {
      id: 'currentEmploymentStatus',
      type: 'select',
      label: 'Employment Status',
      required: true,
      options: [
        'Full-time Employee',
        'Part-time Employee',
        'Contractor',
        'Freelancer',
        'Consultant',
        'Entrepreneur'
      ]
    },
    {
      id: 'industry',
      type: 'text',
      label: 'Industry',
      placeholder: 'e.g., Technology, Healthcare, Education',
      required: false
    },
    
    // Expertise & Skills
    {
      id: 'primaryExpertise',
      type: 'checkbox',
      label: 'Primary Areas of Expertise (Select all that apply)',
      required: true,
      options: [
        'UI/UX Design',
        'Graphic Design',
        'Web Development',
        'Mobile Development',
        'Product Management',
        'Data Science',
        'Marketing',
        'Content Strategy',
        'Branding',
        'Illustration',
        'Photography',
        'Video Production',
        'Frontend Development',
        'Backend Development',
        'Full-Stack Development',
        'DevOps',
        'Game Design',
        'Motion Graphics',
        'User Research'
      ]
    },
    {
      id: 'secondarySkills',
      type: 'checkbox',
      label: 'Secondary Skills',
      required: false,
      options: [
        'Project Management',
        'Team Leadership',
        'Strategic Planning',
        'Business Development',
        'Client Relations',
        'Teaching/Training',
        'Public Speaking',
        'Writing/Documentation'
      ]
    },
    {
      id: 'industriesWorked',
      type: 'checkbox',
      label: 'Industries You\'ve Worked In',
      required: false,
      options: [
        'Technology/Software',
        'Healthcare',
        'Finance/Banking',
        'Education',
        'E-commerce/Retail',
        'Media/Entertainment',
        'Non-profit',
        'Government',
        'Consulting',
        'Startup',
        'Agency',
        'Other'
      ]
    },
    {
      id: 'toolsAndSoftware',
      type: 'checkbox',
      label: 'Tools & Software Expertise',
      required: false,
      options: [
        'Figma',
        'Sketch',
        'Adobe Creative Suite',
        'InVision',
        'Principle',
        'Framer',
        'React',
        'Vue.js',
        'Angular',
        'Node.js',
        'Python',
        'JavaScript',
        'HTML/CSS',
        'Other'
      ]
    },
    
    // Review Experience & Motivation
    {
      id: 'previousReviewExperience',
      type: 'textarea',
      label: 'Previous Review/Mentoring Experience',
      placeholder: 'Describe any previous experience reviewing portfolios, mentoring, or providing feedback...',
      required: false
    },
    {
      id: 'reviewingMotivation',
      type: 'textarea',
      label: 'Why do you want to volunteer as a reviewer?',
      placeholder: 'What motivates you to give back to the design/development community? What do you hope to achieve through this volunteer experience?',
      required: true
    },
    {
      id: 'professionalBio',
      type: 'textarea',
      label: 'Professional Bio',
      placeholder: 'Write a brief professional bio that will be shared with reviewees (2-3 sentences about your background and expertise)...',
      required: true
    },
    
    // Availability & Preferences
    {
      id: 'timezone',
      type: 'select',
      label: 'Timezone',
      required: true,
      options: [
        'Eastern Time (ET)',
        'Central Time (CT)',
        'Mountain Time (MT)',
        'Pacific Time (PT)',
        'Other'
      ]
    },
    {
      id: 'maxReviewsPerDay',
      type: 'select',
      label: 'Maximum Reviews Per Day',
      required: true,
      options: [
        '1 review per day',
        '2 reviews per day',
        '3 reviews per day',
        '4 reviews per day',
        '5+ reviews per day'
      ]
    },
    {
      id: 'preferredReviewDuration',
      type: 'select',
      label: 'Preferred Review Duration',
      required: false,
      options: [
        '30 minutes',
        '45 minutes',
        '60 minutes'
      ]
    },
    {
      id: 'reviewFormat',
      type: 'select',
      label: 'Preferred Review Format',
      required: false,
      options: [
        'Video Call',
        'Phone Call',
        'In-Person (if available)'
      ]
    },
    {
      id: 'availableDays',
      type: 'checkbox',
      label: 'Available Days (Select all that apply)',
      required: true,
      options: [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday'
      ]
    },
    
    // Agreements
    {
      id: 'agreeToTerms',
      type: 'checkbox',
      label: 'Reviewer Terms',
      required: true,
      options: [
        'I agree to the reviewer terms and conditions'
      ]
    },
    {
      id: 'agreeToCommitment',
      type: 'checkbox',
      label: 'Commitment Agreement',
      required: true,
      options: [
        'I commit to attending my scheduled review sessions and providing 24-hour notice if I need to cancel'
      ]
    },
    {
      id: 'agreeToGuidelines',
      type: 'checkbox',
      label: 'Review Guidelines',
      required: true,
      options: [
        'I agree to follow the reviewer guidelines and provide constructive, professional feedback'
      ]
    },
    {
      id: 'agreeToRecording',
      type: 'checkbox',
      label: 'Recording Consent',
      required: false,
      options: [
        'I consent to review sessions being recorded for quality assurance purposes'
      ]
    }
  ]
};

// Basic Feedback Form Template
export const feedbackFormTemplate: FormTemplate = {
  id: 'basic-feedback',
  name: 'Basic Feedback Form',
  description: 'Simple feedback collection form for post-review evaluations',
  category: 'feedback',
  isSystem: true,
  createdAt: new Date().toISOString(),
  fields: [
    {
      id: 'overallRating',
      type: 'radio',
      label: 'Overall Experience Rating',
      required: true,
      options: [
        'Excellent',
        'Very Good',
        'Good',
        'Fair',
        'Poor'
      ]
    },
    {
      id: 'reviewerRating',
      type: 'radio',
      label: 'Reviewer Quality',
      required: true,
      options: [
        'Excellent',
        'Very Good',
        'Good',
        'Fair',
        'Poor'
      ]
    },
    {
      id: 'feedbackQuality',
      type: 'radio',
      label: 'Feedback Quality',
      required: true,
      options: [
        'Very Helpful',
        'Helpful',
        'Somewhat Helpful',
        'Not Very Helpful',
        'Not Helpful'
      ]
    },
    {
      id: 'improvements',
      type: 'checkbox',
      label: 'What aspects could be improved?',
      required: false,
      options: [
        'More time for review',
        'Better scheduling process',
        'More detailed feedback',
        'Technical setup',
        'Communication clarity',
        'Other'
      ]
    },
    {
      id: 'additionalComments',
      type: 'textarea',
      label: 'Additional Comments',
      placeholder: 'Please share any additional thoughts, suggestions, or feedback...',
      required: false
    },
    {
      id: 'recommendToOthers',
      type: 'radio',
      label: 'Would you recommend this service to others?',
      required: true,
      options: [
        'Definitely',
        'Probably',
        'Not Sure',
        'Probably Not',
        'Definitely Not'
      ]
    }
  ]
};

// Contact Information Template
export const contactInfoTemplate: FormTemplate = {
  id: 'contact-info',
  name: 'Contact Information',
  description: 'Basic contact information collection form',
  category: 'custom',
  isSystem: true,
  createdAt: new Date().toISOString(),
  fields: [
    {
      id: 'firstName',
      type: 'text',
      label: 'First Name',
      placeholder: 'Enter your first name',
      required: true
    },
    {
      id: 'lastName',
      type: 'text',
      label: 'Last Name',
      placeholder: 'Enter your last name',
      required: true
    },
    {
      id: 'email',
      type: 'email',
      label: 'Email Address',
      placeholder: 'your.email@example.com',
      required: true
    },
    {
      id: 'phone',
      type: 'text',
      label: 'Phone Number',
      placeholder: '(555) 123-4567',
      required: false
    },
    {
      id: 'organization',
      type: 'text',
      label: 'Organization',
      placeholder: 'Company or institution name',
      required: false
    }
  ]
};

// All available templates
export const systemTemplates: FormTemplate[] = [
  revieweeRegistrationTemplate,
  reviewerRegistrationTemplate,
  feedbackFormTemplate,
  contactInfoTemplate
];

// Helper functions
export const getTemplateById = (id: string): FormTemplate | undefined => {
  return systemTemplates.find(template => template.id === id);
};

export const getTemplatesByCategory = (category: FormTemplate['category']): FormTemplate[] => {
  return systemTemplates.filter(template => template.category === category);
};

export const getAllTemplates = (): FormTemplate[] => {
  return systemTemplates;
};
